/**
 * Freshness state machine (Blueprint A.5, §14 "the green but dead rule").
 * PURE — no DOM. The rendering lives in components/freshness.ts.
 *
 * A runway or balance shown from a stale Plaid sync is a dangerous lie. So:
 *   - every connection stamps "synced 4m ago";
 *   - STALE and DISCONNECTED are their own states, never folded into a
 *     balance color. A disconnected item is not "low balance" — it is
 *     "we do not know", and the UI must say so in words.
 *
 * Plaid consent expires at ~12 months at many US OAuth banks, so
 * ITEM_LOGIN_REQUIRED / PENDING_DISCONNECT are ROUTINE, not exceptional.
 */

export type FreshnessState = 'fresh' | 'syncing' | 'stale' | 'disconnected';

export interface FreshnessInput {
  /** Epoch ms of the last successful sync, or null if never synced. */
  lastSyncedAt: number | null;
  /** Item-level connection health from the Worker. */
  connection: 'ok' | 'reauth_required' | 'pending_disconnect' | 'revoked_by_user' | 'never';
  /** Minutes after which a sync is considered stale. */
  staleAfterMinutes?: number;
  syncing?: boolean;
  /** Injected for testability; defaults to Date.now. */
  now?: number;
}

export interface Freshness {
  state: FreshnessState;
  /** Human phrase, e.g. "synced 4m ago" or "reconnect needed". */
  text: string;
  /** Longer, calm explanation. Never scolding (§14 guardrails). */
  detail?: string;
}

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

export function relativeAge(deltaMs: number): string {
  if (deltaMs < MIN) return 'just now';
  if (deltaMs < HOUR) return `${Math.floor(deltaMs / MIN)}m ago`;
  if (deltaMs < DAY) return `${Math.floor(deltaMs / HOUR)}h ago`;
  return `${Math.floor(deltaMs / DAY)}d ago`;
}

export function computeFreshness(input: FreshnessInput): Freshness {
  const now = input.now ?? Date.now();
  const staleAfter = (input.staleAfterMinutes ?? 180) * MIN;

  // Connection state dominates. A fresh timestamp on a disconnected item is
  // exactly the "green but dead" lie this component exists to prevent.
  if (input.connection === 'reauth_required') {
    return {
      state: 'disconnected',
      text: 'reconnect needed',
      detail:
        'This bank needs you to sign in again before Ballast can see new activity. Balances below are the last known good figures.',
    };
  }
  // Withdrawn at the bank or at Plaid (USER_PERMISSION_REVOKED). As dead as a
  // connection gets, and a client that did not know this status once showed it
  // as "synced 4m ago" — test/unit/freshness.test.ts walks every status.
  if (input.connection === 'revoked_by_user') {
    return {
      state: 'disconnected',
      text: 'reconnect needed',
      detail:
        'Access to this bank was turned off, so Ballast can no longer see it. Balances below are the last known good figures.',
    };
  }
  if (input.connection === 'pending_disconnect') {
    return {
      state: 'stale',
      text: 'reconnect soon',
      detail:
        'This bank will stop sharing data shortly. Reconnecting now keeps the numbers honest.',
    };
  }
  // Checked BEFORE the never-synced branch: a first-ever sync in flight is
  // exactly when the operator most wants to see "syncing", and returning
  // "not synced yet" there reads as though nothing is happening.
  if (input.syncing) {
    return { state: 'syncing', text: 'syncing…' };
  }
  if (input.connection === 'never' || input.lastSyncedAt == null) {
    return { state: 'stale', text: 'not synced yet' };
  }

  const age = Math.max(0, now - input.lastSyncedAt);
  if (age > staleAfter) {
    return {
      state: 'stale',
      text: `synced ${relativeAge(age)}`,
      detail: 'Older than usual — these figures may have moved.',
    };
  }
  return { state: 'fresh', text: `synced ${relativeAge(age)}` };
}
